"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";

import { usePlatformAuth } from "@/hooks/use-platform-auth";
import {
  getLegalScrollObserverRoot,
  scrollToLegalSection,
} from "@/lib/marketing/legal-scroll";
import { cn } from "@/lib/utils";

export type LegalSection = {
  id: string;
  title: string;
};

type LegalDocumentLayoutProps = {
  title: string;
  lastUpdated: string;
  intro: ReactNode;
  sections: LegalSection[];
  children: ReactNode;
  footer?: ReactNode;
};

export function LegalDocumentLayout({
  title,
  lastUpdated,
  intro,
  sections,
  children,
  footer,
}: LegalDocumentLayoutProps) {
  const { isAuthenticated } = usePlatformAuth();
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");

  const onNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      setActiveId(id);
      window.history.pushState(null, "", `#${id}`);
      scrollToLegalSection(id);
    },
    [],
  );

  useEffect(() => {
    const scrollFromHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash || !sections.some((s) => s.id === hash)) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToLegalSection(hash));
      });
    };

    scrollFromHash();
    window.addEventListener("hashchange", scrollFromHash);
    return () => window.removeEventListener("hashchange", scrollFromHash);
  }, [sections]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const scrollRoot = getLegalScrollObserverRoot();
    const rootMargin = scrollRoot
      ? "-24px 0px -55% 0px"
      : "-120px 0px -55% 0px";

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (!el) continue;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) setActiveId(section.id);
        },
        {
          root: scrollRoot,
          rootMargin,
          threshold: 0,
        },
      );
      observer.observe(el);
      observers.push(observer);
    }

    return () => observers.forEach((o) => o.disconnect());
  }, [sections]);

  return (
    <div className="mx-auto flex max-w-6xl gap-10 px-4 pt-2 sm:px-6 sm:pt-4 lg:gap-14 lg:px-8">
      <aside className="hidden w-48 shrink-0 lg:block">
        <nav
          aria-label="Table of contents"
          className={cn(
            "sticky space-y-1",
            "top-6 md:top-8",
          )}
        >
          <p className="mb-4 text-[10px] font-medium uppercase tracking-[0.16em] text-neutral-600">
            On this page
          </p>
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              onClick={(e) => onNavClick(e, section.id)}
              className={cn(
                "block rounded-md px-2 py-1.5 text-[13px] transition-colors",
                activeId === section.id
                  ? "text-white"
                  : "text-neutral-500 hover:text-neutral-300",
              )}
            >
              {section.title}
            </a>
          ))}
        </nav>
      </aside>

      <article className="min-w-0 max-w-165 flex-1 pb-16">
        <header className="mb-10 border-b border-white/6 pb-8">
          <h1 className="text-[clamp(1.75rem,4vw,2.25rem)] font-medium tracking-[-0.02em] text-white">
            {title}
          </h1>
          <p className="mt-4 text-sm text-neutral-500">
            Last updated: {lastUpdated}
          </p>
          <div className="mt-6 text-[15px] leading-[1.86] text-neutral-400 [&_p]:max-w-[64ch]">
            {intro}
          </div>
        </header>

        <div
          className={cn(
            "text-[15px] leading-[1.86] text-neutral-400",
            "[&_section]:scroll-mt-24 [&_section]:pt-9 [&_section]:pb-1",
            "[&_section:first-child]:pt-0",
            "[&_section_h2]:mb-4 [&_section_h2]:text-base [&_section_h2]:font-medium [&_section_h2]:tracking-[-0.01em] [&_section_h2]:text-white",
            "[&_section:not(:last-child)]:border-b [&_section:not(:last-child)]:border-white/6",
            "[&_p]:max-w-[64ch] [&_p]:text-neutral-400",
            "[&_p+p]:mt-4",
            "[&_p>strong:first-child]:mb-1.5 [&_p>strong:first-child]:block [&_p>strong:first-child]:font-medium [&_p>strong:first-child]:text-neutral-200",
            "[&_p+ul]:mt-3 [&_ul+p]:mt-4",
            "[&_ul]:my-4 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:ps-5 [&_ul]:leading-[1.78]",
            "[&_li]:text-neutral-400 [&_li>strong]:font-medium [&_li>strong]:text-neutral-300",
            "[&_a]:text-neutral-200 [&_a]:underline-offset-2 hover:[&_a]:underline",
          )}
        >
          {children}
        </div>

        {footer ? <footer className="pt-10">{footer}</footer> : null}
      </article>
    </div>
  );
}
